import SocketIO from 'socket.io-client'

// Counts subscribers per room and says when the server must be told
export function createRooms(send) {
  const counts = new Map()

  return {
    join(room) {
      const count = counts.get(room) || 0
      counts.set(room, count + 1)
      if (count === 0) send('join', room)
    },
    leave(room) {
      const count = counts.get(room)
      if (!count) return
      if (count === 1) {
        counts.delete(room)
        send('leave', room)
      } else {
        counts.set(room, count - 1)
      }
    },
    rejoin() {
      counts.forEach((count, room) => send('join', room))
    },
  }
}

const openSocket = url => SocketIO(url, { forceNew: true, transports: ['websocket'] })

// One socket per address, opened by the first subscription and closed with the last.
// It does not know what a payload means: every `stock` payload goes to every subscriber.
export function createFeedClient(url, open = openSocket) {
  const handlers = new Set()
  let socket = null
  const rooms = createRooms((action, room) => {
    if (socket && socket.connected) socket.emit('regs', JSON.stringify({ action, list: room }))
  })

  function start() {
    const opened = open(url)
    socket = opened
    // Whether this socket has connected before: a later `connect` follows a drop, and what
    // the feed sent in between was missed
    let { connected } = opened
    opened.on('connect', () => {
      rooms.rejoin()
      if (connected) {
        [...handlers].forEach(entry => {
          // One subscriber's `onRejoin` may end another's subscription
          if (handlers.has(entry) && entry.onRejoin) entry.onRejoin()
        })
      }
      connected = true
    })
    // socket.io reconnects by itself after a lost connection, but not when the server ends the session
    opened.on('disconnect', reason => {
      if (reason === 'io server disconnect') opened.connect()
    })
    opened.on('stock', payload => {
      [...handlers].forEach(entry => entry.handler(payload))
    })
    if (opened.connected) rooms.rejoin()
  }

  function close() {
    const closing = socket
    socket = null
    if (closing) closing.disconnect()
  }

  return {
    // `onRejoin`, if given, is called each time the socket connects again after a drop, once
    // the rooms have been joined again; never for the socket's first connect
    subscribe(room, handler, onRejoin) {
      const entry = { handler, onRejoin }
      handlers.add(entry)
      rooms.join(room)
      if (!socket) start()
      let active = true

      return () => {
        if (!active) return
        active = false
        handlers.delete(entry)
        rooms.leave(room)
        if (handlers.size === 0) close()
      }
    },
  }
}

const clients = new Map()

// The realtime client for an address, shared by every component that asks for it. Not
// vue-socket.io's `$socket`: each module that installs that plugin replaces it for every page,
// so after the portfolio the trading page would join its room on the portfolio's host.
export function feedClient(url) {
  if (!clients.has(url)) clients.set(url, createFeedClient(url))

  return clients.get(url)
}
